import React, { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';
import { MONTHS, buildYearOptions } from '../../../utils/attendanceSummaryAnalytics';

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

const FIELD =
  'w-full h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 text-gray-700';

const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';

// Native <input type="date"> always renders in the browser's locale (mm/dd/yyyy
// here), so we show our own yyyy-mm-dd text field and keep a hidden native input
// purely to drive the calendar popup.
function DateField({ label, value, onChange }) {
  const nativeRef = useRef(null);
  const [text, setText] = useState(value || '');

  useEffect(() => {
    setText(value || '');
  }, [value]);

  const commit = (next) => {
    if (ISO_RE.test(next)) {
      onChange(next);
    } else {
      setText(value || ''); // invalid → snap back
    }
  };

  const openPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        /* fall through */
      }
    }
    el.focus();
  };

  return (
    <div className="min-w-0">
      <span className={LABEL}>{label}</span>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          placeholder="yyyy-mm-dd"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => commit(e.target.value.trim())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit(e.target.value.trim());
          }}
          className={`${FIELD} pr-8`}
        />
        <button
          type="button"
          onClick={openPicker}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-primary-600 transition-colors"
          aria-label={`Pick ${label} date`}
        >
          <Calendar size={14} />
        </button>
        <input
          ref={nativeRef}
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          tabIndex={-1}
          aria-hidden="true"
          className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
        />
      </div>
    </div>
  );
}

function SelectField({ label, value, options, onChange }) {
  return (
    <div className="min-w-0">
      <span className={LABEL}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={FIELD}>
        <option value="All">All</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

export default function FiltersBar({
  year,
  onYearChange,
  activeMonth,
  onMonthSelect,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  filterOptions,
  filters,
  onFilterChange,
}) {
  const years = buildYearOptions();

  return (
    <div className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 shadow-sm space-y-3">
      {/* Row 1 — year + month quick-picks */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={year}
          onChange={(e) => onYearChange(Number(e.target.value))}
          className="h-9 bg-white border border-gray-200 rounded-lg text-xs font-semibold px-2.5 outline-none focus:border-primary-500 text-gray-700 shrink-0"
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <div className="flex flex-wrap gap-1">
          {MONTHS.map((m, i) => (
            <button
              key={m}
              onClick={() => onMonthSelect(i)}
              className={`h-9 px-3 rounded-lg text-xs font-semibold transition-colors ${
                activeMonth === i
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Row 2 — one aligned grid: dates sit in the same columns as the filters */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-3 gap-y-2.5">
        <DateField label="From" value={fromDate} onChange={onFromDateChange} />
        <DateField label="To" value={toDate} onChange={onToDateChange} />
        <SelectField
          label="Location"
          value={filters.location}
          options={filterOptions.locations}
          onChange={(v) => onFilterChange('location', v)}
        />
        <SelectField
          label="Grade"
          value={filters.grade}
          options={filterOptions.grades}
          onChange={(v) => onFilterChange('grade', v)}
        />
        <SelectField
          label="Category"
          value={filters.category}
          options={filterOptions.categories}
          onChange={(v) => onFilterChange('category', v)}
        />
        <SelectField
          label="Designation"
          value={filters.designation}
          options={filterOptions.designations}
          onChange={(v) => onFilterChange('designation', v)}
        />
      </div>
    </div>
  );
}